import { Image } from '../Image';

/**
 * Glassmorphism **surface** — backdrop-blur + double gradient + border +
 * shadow + white text. NO border-radius — each consumer picks its own
 * shape (pill-60 for category pills / My Packs, 28 for PackInfoCard,
 * 20 for tier cards).
 */
export const GLASS_SURFACE =
  'backdrop-blur-[27px] ' +
  'border border-[rgba(171,174,179,0.24)] border-solid ' +
  '[background-image:linear-gradient(180deg,rgba(255,255,255,0.20)_0%,rgba(153,153,153,0.20)_100%),linear-gradient(90deg,rgba(0,0,0,0.18)_0%,rgba(0,0,0,0.18)_100%)] ' +
  'shadow-[0_64px_64px_0_rgba(0,0,0,0.05)] ' +
  'text-white';

/**
 * Convenience alias bundling `GLASS_SURFACE` with the pill radius used by
 * header pills and My Packs (`rounded-[60px]`). Kept for backward-compat
 * with existing consumers.
 */
export const GLASS_CLASSES = GLASS_SURFACE + ' rounded-[60px]';

export interface CategoryPillProps {
  label: string;
  /** Icon asset — typically a photo-rendered ball / mascot. Optional: a
   *  label-only pill is valid (e.g. "All"). */
  iconSrc?: string | undefined;
  active?: boolean;
  onClick?: () => void;
  className?: string;
}

/**
 * A stand-alone design-system primitive. Reused by the Header's category row
 * and by any other surface that needs the same glassy capsule look (filters,
 * tag chips on hero surfaces, etc.).
 *
 * Sizes to content — no forced width. The caller passes extra classes via
 * `className` if a specific Figma measurement is required.
 */
export function CategoryPill({
  label,
  iconSrc,
  active = false,
  onClick,
  className = '',
}: CategoryPillProps) {
  const activeClasses = active
    ? '[border-width:2px] [border-color:var(--co-CategoryPill-activeFill,var(--ui-accent))] ' +
      '[box-shadow:0_0_4px_0_color-mix(in_srgb,var(--co-CategoryPill-activeFill,var(--ui-accent))_30%,transparent),0_64px_64px_0_rgba(0,0,0,0.05)]'
    : '';

  // When icon is present, leave room for the 40×46 icon slot (left:4 + w:40 =
  // ends at 44, plus ~10px gap to the label). Figma measures label start at
  // 54.5px from the pill's left edge — we round to 56 for strip alignment.
  const paddingLeft = iconSrc ? 'pl-[56px]' : 'pl-5';

  // Idle text color: inherit from GLASS_SURFACE (text-white) but allow DS override.
  // Active text color: similarly overrideable.
  const textColor = active
    ? 'var(--co-CategoryPill-activeText, var(--color-text-on-brand, #fff))'
    : 'var(--co-CategoryPill-idleText, #fff)';

  // Surface fill: ColorField produces hex/rgba, so overrides land on the
  // `background` shorthand (a hex isn't a valid `background-image` value).
  //   • Idle  — fallback `transparent` keeps GLASS_SURFACE's blur + border
  //             + shadow without the two-layer tint (preserves the
  //             pre-existing default look).
  //   • Active — fallback reproduces the GLASS_SURFACE two-layer tint
  //             verbatim, so an un-overridden active pill renders
  //             pixel-identically. Operators retint via the knob.
  const surfaceStyle = active
    ? {
        background:
          'var(--co-CategoryPill-activeBackground, ' +
          'linear-gradient(180deg,rgba(255,255,255,0.20) 0%,rgba(153,153,153,0.20) 100%),' +
          'linear-gradient(90deg,rgba(0,0,0,0.18) 0%,rgba(0,0,0,0.18) 100%))',
      }
    : { background: 'var(--co-CategoryPill-idleFill, transparent)' };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-co-component="CategoryPill"
      style={{ color: textColor, ...surfaceStyle }}
      className={
        GLASS_SURFACE +
        ' [border-radius:var(--co-CategoryPill-borderRadius,60px)]' +
        ' relative h-11 cursor-pointer overflow-hidden ' +
        '[transition:border-color_180ms_ease,box-shadow_220ms_ease,filter_160ms_ease,transform_120ms_ease] ' +
        'hover:brightness-110 active:scale-[0.98] ' +
        'type-body-semi ' +
        paddingLeft +
        ' pr-5 ' +
        activeClasses +
        ' ' +
        className
      }
    >
      {iconSrc && (
        // Icon slot: 40×46 — taller than the 44-tall pill so its bottom is
        // clipped by the pill (rounded-[60px] + overflow-hidden), matching
        // the Figma "ball sinks into the pill" look. Vertically centered
        // with a +7px downward nudge (Figma: top = 50% + 7). The inner
        // <img> is oversized so the icon bleeds outside the slot, then
        // re-clipped by the slot's own overflow-hidden.
        <Image
          src={iconSrc}
          alt=""
          className="pointer-events-none absolute left-[4px] h-[46px] w-[40px] top-[calc(50%+7px)] -translate-y-1/2"
          imgClassName="absolute block max-w-none"
          imgStyle={{
            height: '164%',
            width: '178%',
            left: '-39%',
            top: '-37%',
          }}
        />
      )}

      <span className="relative">{label}</span>

      <span
        aria-hidden
        className={
          'pointer-events-none absolute inset-0 rounded-[60px] transition-opacity duration-200 ' +
          '[box-shadow:inset_0_0_7px_0_color-mix(in_srgb,var(--co-CategoryPill-activeFill,var(--ui-accent))_60%,transparent)] ' +
          (active ? 'opacity-100' : 'opacity-0')
        }
      />
    </button>
  );
}
